/**
 * Chat API Utility
 * Handles communication with backend chat endpoints
 */

import { getAuthHeaders } from './auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5444';

interface CreateConversationResponse {
  conversationId: number;
  createdAt: string;
}

interface SendMessageResponse {
  chatResponse?: string;
  conversationId: number;
}

interface ApiResponse<T> {
  status: 'success' | 'error';
  message: string;
  data: T | null;
}

/**
 * Create a new conversation for a specific day
 */
export async function createConversation(
  day: number,
  companyGameId?: number
): Promise<number> {
  try {
    const body: { day: number; companyGameId?: number } = { day };
    if (companyGameId !== undefined) {
      body.companyGameId = companyGameId;
    }

    const response = await fetch(`${API_URL}/api/chat/conversations`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });

    // Handle authentication errors
    if (response.status === 401 || response.status === 403) {
      console.error('[Auth] Token expired or invalid, reloading page...');
      window.location.reload();
      throw new Error('Authentication failed');
    }

    const result: ApiResponse<CreateConversationResponse> = await response.json();

    if (result.status === 'error' || !result.data) {
      throw new Error(result.message || 'Failed to create conversation');
    }

    return result.data.conversationId;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
}

/**
 * Send a message and get AI response
 */
export async function sendMessage(
  conversationId: number,
  message: string
): Promise<string> {
  try {
    const response = await fetch(`${API_URL}/api/chat/message`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        conversationId,
        message,
        mode: 'chat'
      })
    });

    // Handle authentication errors
    if (response.status === 401 || response.status === 403) {
      console.error('[Auth] Token expired or invalid, reloading page...');
      window.location.reload();
      throw new Error('Authentication failed');
    }

    const result: ApiResponse<SendMessageResponse> = await response.json();

    if (result.status === 'error' || !result.data) {
      throw new Error(result.message || 'Failed to send message');
    }

    return result.data.chatResponse || '';
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
}
